import { useEffect, useRef, useState, type ReactNode } from "react";

import { Image as ImageIcon, MessageSquare, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { Locale } from "@/i18n/config";
import { useTranslation } from "@/i18n/use-translation";
import type { TFunction } from "@/i18n/catalog";
import { formatDateTime, formatRelative } from "@/lib/format";
import { foodLabel } from "@/lib/food-labels";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { MentionComposer } from "@/components/waste/mention-composer";

import type { WasteEvent } from "@/hooks/use-waste-events";
import { showApiErrorAs } from "@/lib/api-error";
import {
  canDeleteComment,
  canEditComment,
  useCreateComment,
  useDeleteComment,
  useMentionableMembers,
  useUpdateComment,
  useWasteEventComments,
  type MentionRef,
  type WasteEventComment,
} from "@/hooks/use-waste-event-comments";

type Props = {
  event: WasteEvent | null;
  currentUserId: string | null;
  isModerator: boolean;
  onClose: () => void;
  // Deep-link target: scroll this comment into view + highlight it (POS2-761).
  scrollToCommentId?: string | null;
  // POS2-849: the full event card, rendered at the top of the panel so a
  // notification opens straight to the event + its actions + thread (no extra
  // click). When omitted, a compact thumbnail context header is shown instead.
  eventCard?: ReactNode;
};

const TEXTAREA_CLASS =
  "min-h-[60px] w-full resize-y rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500";

/** Which waste event this thread is about — so a deep-linked panel is identifiable (POS2-847).
 * Takes `t` because the last two rungs are copy rather than data (POS2-1134 CP5). */
function eventTitle(t: TFunction, event: WasteEvent): string {
  return (
    foodLabel(t, event.inventory_item_name) ??
    foodLabel(t, event.item?.label) ??
    foodLabel(t, event.subcategory_name) ??
    foodLabel(t, event.category_name) ??
    (event.classification_level === 1
      ? t("waste.eventTitleUnidentified")
      : t("waste.eventTitleFallback"))
  );
}

function eventMeta(locale: Locale, event: WasteEvent): string {
  const when = formatDateTime(locale, event.logged_at, {
    dateStyle: undefined,
    timeStyle: undefined,
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: event.timezone ?? "UTC",
  });
  return [`${event.item_weight.toFixed(2)} kg`, event.kitchen_name, when]
    .filter(Boolean)
    .join(" · ");
}

export function CommentsPanel({
  event,
  currentUserId,
  isModerator,
  onClose,
  scrollToCommentId = null,
  eventCard,
}: Props) {
  const { locale, t } = useTranslation();
  const eventId = event?.id ?? null;
  const commentsQuery = useWasteEventComments(eventId);
  const membersQuery = useMentionableMembers(eventId);
  const createMutation = useCreateComment();
  const updateMutation = useUpdateComment();
  const deleteMutation = useDeleteComment();

  const [postNonce, setPostNonce] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Clear transient state whenever the panel switches events or closes.
  useEffect(() => {
    setEditingId(null);
    setEditDraft("");
  }, [eventId]);

  const comments = commentsQuery.data ?? [];

  // Deep-link: once the thread is loaded, scroll the target comment into view
  // and highlight it briefly (POS2-761).
  useEffect(() => {
    if (!scrollToCommentId) return;
    const el = listRef.current?.querySelector(`[data-comment-id="${scrollToCommentId}"]`);
    if (!el) return;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    setHighlightId(scrollToCommentId);
    const timer = setTimeout(() => setHighlightId(null), 2500);
    return () => clearTimeout(timer);
  }, [scrollToCommentId, commentsQuery.data]);

  function handleSaveEdit() {
    if (!eventId || !editingId || !editDraft.trim()) return;
    updateMutation.mutate(
      { eventId, commentId: editingId, body: editDraft.trim() },
      {
        onSuccess: () => {
          setEditingId(null);
          setEditDraft("");
        },
        onError: (e: Error) => showApiErrorAs(t("waste.commentPostFailed"), e),
      },
    );
  }

  function handleDelete(comment: WasteEventComment) {
    if (!eventId || !confirm(t("waste.deleteCommentConfirm"))) return;
    deleteMutation.mutate(
      { eventId, commentId: comment.id },
      {
        onSuccess: () => toast.success(t("waste.commentDeleted")),
        onError: (e: Error) => showApiErrorAs(t("waste.commentEditFailed"), e),
      },
    );
  }

  return (
    <Sheet open={event !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className={cn("w-full gap-0 p-0", eventCard ? "sm:max-w-2xl lg:max-w-4xl" : "sm:max-w-md")}
        data-testid="comments-panel"
      >
        <SheetHeader className="border-b border-gray-100">
          <SheetTitle className="text-sm">{t("waste.comments")}</SheetTitle>
          <SheetDescription className="sr-only">{t("waste.commentsDescription")}</SheetDescription>
          {event && !eventCard && (
            <div className="mt-1 flex items-center gap-2.5" data-testid="comments-event-context">
              <EventContextBody event={event} />
            </div>
          )}
        </SheetHeader>

        <div className={cn("flex min-h-0 flex-1 flex-col", eventCard && "lg:flex-row")}>
          {eventCard && (
            <div className="max-h-[45vh] shrink-0 overflow-y-auto border-b border-gray-100 p-4 lg:max-h-none lg:w-[400px] lg:border-b-0 lg:border-r">
              {eventCard}
            </div>
          )}

          <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 overflow-y-auto p-4">
              {commentsQuery.isLoading && <ThreadSkeleton />}
              {commentsQuery.isError && (
                <p className="text-sm text-red-600">{t("waste.commentsLoadFailed")}</p>
              )}
              {!commentsQuery.isLoading && !commentsQuery.isError && comments.length === 0 && (
                <EmptyThread />
              )}
              {comments.length > 0 && (
                <ul className="flex flex-col gap-4" ref={listRef}>
                  {comments.map((comment) => (
                    <li
                      key={comment.id}
                      className={`flex flex-col gap-1 transition-shadow ${
                        highlightId === comment.id ? "rounded-md ring-2 ring-blue-400" : ""
                      }`}
                      data-comment-id={comment.id}
                      data-testid="comment"
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-sm font-medium text-gray-900">
                          {authorLabel(t, comment)}
                        </span>
                        <time
                          className="shrink-0 text-xs text-gray-400"
                          dateTime={comment.created_at}
                          title={formatDateTime(locale, comment.created_at)}
                        >
                          {formatRelative(locale, comment.created_at)}
                        </time>
                      </div>

                      {editingId === comment.id ? (
                        <div className="flex flex-col gap-2">
                          <textarea
                            className={TEXTAREA_CLASS}
                            value={editDraft}
                            onChange={(e) => setEditDraft(e.target.value)}
                            aria-label={t("waste.editComment")}
                          />
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              className="h-7 text-xs"
                              onClick={handleSaveEdit}
                              disabled={updateMutation.isPending || !editDraft.trim()}
                            >
                              {t("common.save")}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-xs text-gray-500"
                              onClick={() => {
                                setEditingId(null);
                                setEditDraft("");
                              }}
                            >
                              {t("common.cancel")}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <CommentBody body={comment.body} mentions={comment.mentions} />
                          {(canEditComment(comment, currentUserId) ||
                            canDeleteComment(comment, currentUserId, isModerator)) && (
                            <div className="flex items-center gap-1">
                              {canEditComment(comment, currentUserId) && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 gap-1 px-1.5 text-xs text-gray-400 hover:text-gray-600"
                                  onClick={() => {
                                    setEditingId(comment.id);
                                    setEditDraft(comment.body);
                                  }}
                                  data-testid="comment-edit"
                                >
                                  <Pencil className="size-3" aria-hidden />
                                  {t("waste.edit")}
                                </Button>
                              )}
                              {canDeleteComment(comment, currentUserId, isModerator) && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 gap-1 px-1.5 text-xs text-gray-400 hover:text-red-600"
                                  onClick={() => handleDelete(comment)}
                                  data-testid="comment-delete"
                                >
                                  <Trash2 className="size-3" aria-hidden />
                                  {t("stock.delete")}
                                </Button>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <MentionComposer
              key={`${eventId}-${postNonce}`}
              members={membersQuery.data ?? []}
              isPending={createMutation.isPending}
              onSubmit={(body, mentions) => {
                if (!eventId) return;
                createMutation.mutate(
                  { eventId, body, mentions },
                  {
                    onSuccess: () => setPostNonce((n) => n + 1),
                    onError: (e: Error) => showApiErrorAs(t("waste.commentDeleteFailed"), e),
                  },
                );
              }}
            />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/** The event thumbnail + title/meta shown in the panel header (POS2-847). */
function EventContextBody({ event }: { event: WasteEvent }) {
  const { locale, t } = useTranslation();
  return (
    <>
      {event.image_read_url ? (
        <img
          src={event.image_read_url}
          alt=""
          className="size-9 shrink-0 rounded object-cover"
          loading="lazy"
        />
      ) : (
        <div className="flex size-9 shrink-0 items-center justify-center rounded bg-gray-100 text-gray-300">
          <ImageIcon className="size-4" aria-hidden />
        </div>
      )}
      <div className="min-w-0 text-left">
        <p className="truncate text-xs font-medium text-gray-900">{eventTitle(t, event)}</p>
        <p className="truncate text-[11px] text-gray-500">{eventMeta(locale, event)}</p>
      </div>
    </>
  );
}

function authorLabel(t: TFunction, comment: WasteEventComment): string {
  return comment.author_name ?? comment.author_email ?? t("waste.commentAuthorUnknown");
}

/** Render the comment body, highlighting `@Name` tokens for its mentions. */
function CommentBody({ body, mentions }: { body: string; mentions: MentionRef[] }) {
  const names = [...new Set(mentions.map((m) => m.name).filter((n): n is string => !!n))].sort(
    (a, b) => b.length - a.length,
  );
  if (names.length === 0) {
    return <p className="whitespace-pre-wrap text-sm text-gray-700">{body}</p>;
  }
  const tokens = new Set(names.map((n) => `@${n}`));
  const parts = body.split(new RegExp(`(@(?:${names.map(escapeRegExp).join("|")}))`, "g"));
  return (
    <p className="whitespace-pre-wrap text-sm text-gray-700">
      {parts.map((part, i) =>
        tokens.has(part) ? (
          <span key={i} className="font-medium text-blue-600">
            {part}
          </span>
        ) : (
          part
        ),
      )}
    </p>
  );
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function EmptyThread() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center py-10 text-center">
      <MessageSquare className="size-10 text-gray-300" aria-hidden />
      <p className="mt-3 text-sm font-medium text-gray-900">{t("waste.noComments")}</p>
      <p className="mt-1 max-w-sm text-sm text-gray-500">{t("waste.noCommentsHint")}</p>
    </div>
  );
}

function ThreadSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex flex-col gap-1.5">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      ))}
    </div>
  );
}
